import {
  DITypes,
  IDefaultHttpFinancasResponse,
  TDefaultPaginationFinancasHttp
} from "@/core/@types/types";
import useExportarBlobFinancasApi from "@/core/composables/download/useExportarBlobFinancasApi";
import {
  FinancasDevHttpDi,
  type IFinancasDevHttp
} from "@/core/services/FinancasDevHttp";
import { inject } from "inversify";
import {
  PainelDeIntegracaoesGateway,
  TArquivoDetalhesIntegracao,
  TChamarServicoParams,
  TChamarServicoResponse,
  THistoricoIntegracao,
  TKeyValue,
  TObterHistoricoIntegracaoParams,
  TObterWebservicesParams,
  TWebservice
} from "./PainelDeIntegracaoesGateway";
import { type HttpClient } from "@/core/services/protocols/types";
import { normalizeGatewayUrl } from "../widgets/chamarServico/composables/chamarServicoRequest";

export default class PainelDeIntegracoesGatewayAdapter implements PainelDeIntegracaoesGateway {
  @inject(DITypes.FINANCAS_HTTP_CLIENT) private httpClient!: HttpClient;
  @inject(DITypes.FINANCAS_API_HTTP_CLIENT)
  private httpClientFinancasApi!: HttpClient;

  @inject(DITypes.INDICADORES_HTTP_CLIENT)
  private httpClientIndicadores!: HttpClient;

  @inject(FinancasDevHttpDi) private financasDevHttp!: IFinancasDevHttp;

  async obterWebservices(
    params: TObterWebservicesParams
  ): Promise<IDefaultHttpFinancasResponse<TWebservice[]> | undefined> {
    const queryParams = {
      api: params.api,
      inativo: params.inativo,
      integrationType: params.integrationType,
      page: params.page,
      pageSize: params.pageSize,
      group: params.group,
      filter: params.filter,
      sort: params.sort
    };
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/WebserviceAPI/ListGrid`,
        queryParams
      );

      if (!response.Data)
        return {
          Data: [],
          Total: 0,
          Ok: false
        };

      return {
        Data: response.Data,
        Total: response.Total ?? 0,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<TWebservice[]>
    >({
      url: "/v1/WebserviceAPI/ListGrid",
      queryParams: queryParams
    });

    if (response.statusCode != 200)
      return {
        Data: [],
        Total: 0,
        Ok: false
      };

    return {
      Data: response.body?.Data || [],
      Total: response.body?.Total ?? 0,
      Ok: true
    };
  }

  async obterListaWebservices(
    params?: TDefaultPaginationFinancasHttp
  ): Promise<IDefaultHttpFinancasResponse<TKeyValue[]> | undefined> {
    const queryParams = {
      page: params?.page,
      pageSize: params?.pageSize,
      group: params?.group,
      filter: params?.filter,
      sort: params?.sort
    };

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/WebserviceAPI/ObterListaWebservices`,
        queryParams
      );

      if (!response.Data)
        return {
          Result: [],
          Ok: false
        };

      return {
        Result: response.Data,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<TKeyValue[]>
    >({
      url: "/v1/WebserviceAPI/ObterListaWebservices",
      queryParams: queryParams
    });

    if (response.statusCode != 200)
      return {
        Result: [],
        Ok: false
      };

    return {
      Result: response.body?.Data || response.body?.Result || [],
      Ok: true
    };
  }

  async obterHistoricoIntegracao(
    params: TObterHistoricoIntegracaoParams
  ): Promise<IDefaultHttpFinancasResponse<THistoricoIntegracao[]> | undefined> {
    const queryParams = {
      serviceId: params.serviceId,
      data: params.data,
      // eslint-disable-next-line camelcase
      client_id: params.client_id,
      page: params.page,
      pageSize: params.pageSize,
      sort: params.sort,
      filter: params.filter,
      group: params.group
    };

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/LogWebserviceAPI/ObterLog`,
        queryParams
      );

      if (!response.Data)
        return {
          Result: [],
          Ok: false,
          Total: 0
        };

      return {
        Result: response.Data,
        Ok: true,
        Total: response.Total ?? 0
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<THistoricoIntegracao[]>
    >({
      url: "/v1/LogWebserviceAPI/ObterLog",
      queryParams: queryParams
    });

    if (response.statusCode != 200)
      return {
        Result: [],
        Ok: false
      };

    return {
      Result: response.body?.Data || response.body?.Result || [],
      Ok: true,
      Total: response.body?.Total ?? 0
    };
  }

  async obterDetalhesArquivoIntegracao(
    arquivoId: string
  ): Promise<
    IDefaultHttpFinancasResponse<TArquivoDetalhesIntegracao> | undefined
  > {
    const queryParams = { id: arquivoId };

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/ArquivoAPI/ObterDetalhesDoArquivo`,
        queryParams
      );

      return {
        Result: response.Result,
        Error: response.Error,
        Ok: response.Ok !== false
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<TArquivoDetalhesIntegracao>
    >({
      url: "/v1/ArquivoAPI/ObterDetalhesDoArquivo",
      queryParams
    });

    if (response.statusCode != 200)
      return {
        Ok: false
      };

    return {
      Result: response.body?.Result,
      Error: response.body?.Error,
      Ok: response.body?.Ok !== false
    };
  }

  async baixarArquivosIntegracao(arquivoIds: string[]): Promise<void> {
    const arquivos = arquivoIds.filter(Boolean).join(",");

    if (!arquivos) return;

    const { exportarBlobFinancasApi } = useExportarBlobFinancasApi();

    await exportarBlobFinancasApi(
      `/v1/ArquivoAPI/Download?arquivos=${encodeURIComponent(arquivos)}`
    );
  }

  async deletarIntegracao(
    id: string
  ): Promise<IDefaultHttpFinancasResponse<boolean> | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        `/api/v1/WebserviceAPI/Delete`,
        JSON.stringify(id),
        { headers: { "Content-Type": "application/json" } }
      );

      if (!response.Ok)
        return {
          Result: false,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: response.Ok,
        Error: response.Error,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.post<
      IDefaultHttpFinancasResponse<boolean>
    >({
      url: "/v1/WebserviceAPI/Delete",
      body: JSON.stringify(id),
      config: { headers: { "Content-Type": "application/json" } }
    });

    if (!response.body?.Ok)
      return {
        Result: false,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result: response.body?.Result || false,
      Error: response.body?.Error,
      Ok: true
    };
  }

  async toggleIntegracao(
    id: string,
    toggle: boolean
  ): Promise<IDefaultHttpFinancasResponse<boolean> | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        `/api/v1/WebserviceAPI/Toggle`,
        { id: id, toggle: toggle }
      );

      if (!response.Ok)
        return {
          Result: false,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: response.Ok,
        Error: response.Error,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.post<
      IDefaultHttpFinancasResponse<boolean>
    >({
      url: "/v1/WebserviceAPI/Toggle",
      body: { id: id, toggle: toggle }
    });

    if (!response.body?.Ok)
      return {
        Result: false,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result: response.body?.Result || false,
      Error: response.body?.Error,
      Ok: true
    };
  }

  async chamarServico(
    params: TChamarServicoParams,
    url: string
  ): Promise<
    IDefaultHttpFinancasResponse<TChamarServicoResponse | boolean> | undefined
  > {
    const normalizedUrl = normalizeGatewayUrl(url);

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        `/api${normalizedUrl}`,
        params
      );

      if (!response.Ok)
        return {
          Result: false,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: response.Result,
        Error: response.Error,
        Ok: true
      };
    }

    const httpClient = normalizedUrl.startsWith("/v1/")
      ? this.httpClientFinancasApi
      : this.httpClient;

    const response = await httpClient.post<
      IDefaultHttpFinancasResponse<TChamarServicoResponse | boolean>
    >({
      url: normalizedUrl,
      body: params
    });

    if (!response.body?.Ok)
      return {
        Result: response.body?.Result || false,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result: response.body?.Result || false,
      Error: response.body?.Error,
      Ok: true
    };
  }
}
